import React, { useMemo } from 'react';
import SectionCard from './SectionCard.jsx';
import MetricHorizonTable from './MetricHorizonTable.jsx';
import EmptyState from './EmptyState.jsx';
import { buildKPIsTimeline, buildFinancialRatiosTimeline, getTabDirection, verbosityAtLeast } from '../engine.js';

/* v8 Path Q — ComparativeTablesView
   A universal "tables everywhere" sub-view. Given a moduleId it renders:
     1. The module's KPI timeline (from MODULE_KPI_REGISTRY)
     2. The Financial Ratios timeline (same 15 ratios, always shown)
   Both respect ScenarioBar's Period × Density × Direction. Same visual
   language — sticky headers, current-period gold tint, sparkline-less
   dense rows. Answers the "where are the tables?" question: every metric
   is now viewable as a rolling horizon table, per module.

   ONE component. Every module uses it. Extending is just adding more
   entries to MODULE_KPI_REGISTRY[moduleId] in engine.js. */
const MODULE_TITLE = {
  actuals:   'Actuals',
  budgets:   'Budgets',
  forecasts: 'Forecasts',
  synthesis: 'Synthesis',
  fcc:       'FCC',
};

export default function ComparativeTablesView({ scenario, moduleId }) {
  const direction = getTabDirection(scenario, moduleId);
  const dirLabel = direction === 'past' ? 'trailing' : direction === 'future' ? 'leading' : 'spanning';
  const title = MODULE_TITLE[moduleId] || moduleId;

  const kpiTimeline = useMemo(
    () => buildKPIsTimeline(scenario, moduleId),
    [scenario, moduleId]
  );
  const ratiosTimeline = useMemo(
    () => buildFinancialRatiosTimeline(scenario, { direction }),
    [scenario, direction]
  );

  const hasKPIs = kpiTimeline.rows.length > 0;
  /* v8 Path Q/U — DRY sparkline + gradient pipe. Each viz is gated by:
     - verbosity:   Minimal hides sparkline visuals entirely
     - ScenarioBar: per-toggle controls for Trend column + Row sparkline
     - gradient:    area-fill under row/trend sparklines */
  const minVerbosity = verbosityAtLeast(scenario, 'standard');
  const showSparkline    = minVerbosity && scenario?.showTrendColumn !== false;
  const showRowSparkline = minVerbosity && scenario?.showRowSparkline !== false;
  const gradient = !!scenario?.sparklineGradient;
  /* v8 Path AI — semantic tone toggle from ScenarioBar. When ON,
     sparkline colors reflect each row's `betterWhen` polarity. */
  const semanticTone = !!scenario?.sparklineSemanticTone;

  return (
    <div className="flex flex-col gap-4">
      {hasKPIs ? (
        <SectionCard
          sectionId="comparative"
          title={`${title} — KPIs × horizon`}
          subtitle={`${kpiTimeline.rows.length} metrics × ${kpiTimeline.periods} periods of ${kpiTimeline.density}-month each (${dirLabel}). Every chart on the ${title} overview has a comparative table here. Period × Density × Direction are controlled from the model context bar above.`}
        >
          <MetricHorizonTable
            title={`${title} KPIs across horizon`}
            entity="Current Lens Entity"
            subtitle={`Module: ${title} · ${dirLabel}`}
            periods={kpiTimeline.periodLabels}
            currentPeriodLabel={kpiTimeline.currentLabel}
            currentPeriodProof={kpiTimeline.currentState}
            rows={kpiTimeline.rows}
            showSparkline={showSparkline}
            showRowSparkline={showRowSparkline}
            gradient={gradient}
            semanticTone={semanticTone}
            sparkIdPrefix={`mh-${moduleId}-kpi`}
          />
        </SectionCard>
      ) : (
        <SectionCard
          sectionId="comparative"
          title={`${title} — Comparative Tables`}
          subtitle="No KPI registry configured for this module yet."
        >
          <EmptyState
            icon="≡"
            heading="No KPIs wired for this module"
            body={`Add entries to MODULE_KPI_REGISTRY.${moduleId} in engine.js. Each entry is { key, label, category, format, tone, extract } — extract(row, {is, bs, cf}) returns the value at one period.`}
          />
        </SectionCard>
      )}

      <SectionCard
        title={`${title} — Financial Ratios × horizon`}
        subtitle={`15 ratios × ${ratiosTimeline.periods} periods of ${ratiosTimeline.density}-month each (${dirLabel}). Same ratios, any window. Universal across every module.`}
      >
        <MetricHorizonTable
          title="Financial Ratios across horizon"
          entity="Current Lens Entity"
          subtitle={`${ratiosTimeline.periods} periods × ${ratiosTimeline.density}-month · ${ratiosTimeline.direction}`}
          periods={ratiosTimeline.periodLabels}
          currentPeriodLabel={ratiosTimeline.currentLabel}
          currentPeriodProof={ratiosTimeline.currentState}
          rows={ratiosTimeline.rows}
          showSparkline={showSparkline}
          showRowSparkline={showRowSparkline}
          gradient={gradient}
          semanticTone={semanticTone}
          sparkIdPrefix={`mh-${moduleId}-ratios`}
        />
      </SectionCard>

      <p className="text-[0.72rem] text-text-dim italic">
        Comparative Tables is the universal table view: every KPI, every ratio, every metric — DRY across all modules. Sparkline gradient
        and verbosity toggles live in the model context bar. This view is read-only; to change a value, jump back to the owning sub-section.
      </p>
    </div>
  );
}
